import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icons from '../components/Icons';
import toast from 'react-hot-toast';

export default function EditProfilePage() {
  const { user, updateUserName, resetPassword, deleteAccount } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || '');
  const [updatingName, setUpdatingName] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleUpdateName = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    setUpdatingName(true);
    try {
      await updateUserName(name.trim());
      toast.success('Username updated!');
    } catch (err) {
      toast.error('Failed to update username.');
    } finally {
      setUpdatingName(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setResettingPw(true);
    try {
      await resetPassword(user.email);
      toast.success(`Reset email sent to ${user.email} ✉️`);
    } catch (err) {
      toast.error('Failed to send reset email.');
    } finally {
      setResettingPw(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success('Account permanently deleted.');
      navigate('/');
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        toast.error('Sign out and sign back in first, then try again.');
      } else {
        toast.error('Failed to delete account. Please try again.');
      }
      setShowDeleteConfirm(false);
      setDeleting(false);
    }
  };

  const avatarUrl = user?.photoURL || `https://api.dicebear.com/7.x/shapes/svg?seed=${user?.uid}`;

  return (
    <>
      <style>{`
        .ep-page { flex: 1; overflow-y: auto; padding: 0; }
        .ep-hero {
          background: linear-gradient(135deg, rgba(109,40,217,0.12), rgba(139,92,246,0.06));
          border-bottom: 1px solid var(--b1);
          padding: 28px 28px 24px;
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .ep-avatar {
          width: 72px; height: 72px;
          border-radius: 50%;
          border: 3px solid rgba(139,92,246,0.4);
          object-fit: cover;
          flex-shrink: 0;
          box-shadow: 0 0 0 6px rgba(109,40,217,0.08);
        }
        .ep-hero-info { flex: 1; min-width: 0; }
        .ep-hero-name {
          font-size: 20px; font-weight: 800;
          color: var(--t1);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          margin-bottom: 3px;
        }
        .ep-hero-email { font-size: 13px; color: var(--t3); }
        .ep-back {
          display: flex; align-items: center; gap: 7px;
          padding: 8px 16px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--b2);
          border-radius: var(--r2);
          color: var(--t2); font-size: 13px; font-weight: 600;
          cursor: pointer;
          transition: var(--transition);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ep-back:hover { background: var(--s2); color: var(--t1); transform: translateX(-2px); }
        .ep-body { padding: 24px; max-width: 640px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
        .ep-card {
          background: var(--s1);
          border: 1px solid var(--b1);
          border-radius: var(--r3);
          overflow: hidden;
        }
        .ep-card-header {
          padding: 16px 20px;
          border-bottom: 1px solid var(--b1);
          display: flex; align-items: center; gap: 10px;
        }
        .ep-card-icon {
          width: 34px; height: 34px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .ep-card-title { font-size: 14px; font-weight: 700; color: var(--t1); }
        .ep-card-sub { font-size: 11px; color: var(--t3); margin-top: 1px; }
        .ep-card-body { padding: 20px; display: flex; flex-direction: column; gap: 14px; }
        .ep-field label {
          display: block;
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
          text-transform: uppercase; color: var(--t3);
          margin-bottom: 8px;
        }
        .ep-input-row { display: flex; gap: 10px; align-items: stretch; }
        .ep-input {
          flex: 1;
          padding: 11px 14px;
          background: var(--s2);
          border: 1px solid var(--b2);
          border-radius: var(--r2);
          color: var(--t1);
          font-size: 13px;
          font-family: var(--f);
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .ep-input:focus { border-color: var(--ac); box-shadow: 0 0 0 3px var(--acg); }
        .ep-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .ep-input-hint { font-size: 11px; color: var(--t3); margin-top: 6px; line-height: 1.5; }
        .ep-save-btn {
          padding: 11px 20px;
          background: linear-gradient(135deg, var(--ac), #7c3aed);
          border: none; border-radius: var(--r2);
          color: #fff; font-size: 13px; font-weight: 700;
          cursor: pointer;
          transition: var(--transition);
          white-space: nowrap;
          box-shadow: 0 4px 16px rgba(109,40,217,0.3);
          flex-shrink: 0;
        }
        .ep-save-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(109,40,217,0.45); }
        .ep-save-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
        .ep-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 16px;
          background: var(--s2);
          border: 1px solid var(--b2);
          border-radius: var(--r2);
          gap: 16px;
        }
        .ep-row-info { flex: 1; min-width: 0; }
        .ep-row-title { font-size: 14px; font-weight: 600; color: var(--t1); margin-bottom: 2px; }
        .ep-row-desc { font-size: 11px; color: var(--t3); line-height: 1.5; }
        .ep-secondary-btn {
          padding: 9px 18px;
          background: transparent;
          border: 1px solid var(--b2);
          border-radius: var(--r2);
          color: var(--t2); font-size: 12px; font-weight: 700;
          cursor: pointer;
          transition: var(--transition);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ep-secondary-btn:hover:not(:disabled) { background: var(--s3); border-color: var(--b3); color: var(--t1); }
        .ep-secondary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .ep-danger-card {
          background: rgba(239,68,68,0.03);
          border: 1px solid rgba(239,68,68,0.18);
          border-radius: var(--r3);
          overflow: hidden;
        }
        .ep-danger-header {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(239,68,68,0.12);
          display: flex; align-items: center; gap: 10px;
        }
        .ep-danger-row {
          padding: 16px 20px;
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
        }
        .ep-danger-btn {
          padding: 9px 18px;
          background: rgba(239,68,68,0.08);
          border: 1px solid rgba(239,68,68,0.25);
          border-radius: var(--r2);
          color: #f87171; font-size: 12px; font-weight: 700;
          cursor: pointer;
          transition: var(--transition);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ep-danger-btn:hover { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.4); transform: translateY(-1px); }

        /* Responsive */
        @media (max-width: 600px) {
          .ep-hero { flex-wrap: wrap; gap: 16px; padding: 20px 16px; }
          .ep-hero-name { font-size: 17px; }
          .ep-body { padding: 16px; gap: 12px; }
          .ep-input-row { flex-direction: column; }
          .ep-save-btn { width: 100%; }
          .ep-row { flex-direction: column; align-items: flex-start; gap: 12px; }
          .ep-secondary-btn { width: 100%; text-align: center; }
          .ep-danger-row { flex-direction: column; align-items: flex-start; gap: 12px; }
          .ep-danger-btn { width: 100%; text-align: center; }
        }
      `}</style>

      <div className="main">
        {/* Top bar */}
        <div className="mtbar">
          <button className="ep-back" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--t1)', marginLeft: 8 }}>Edit Profile</div>
        </div>

        <div className="ep-page">
          {/* Hero header */}
          <div className="ep-hero">
            <img src={avatarUrl} alt="" className="ep-avatar" />
            <div className="ep-hero-info">
              <div className="ep-hero-name">{user?.name || 'Your Account'}</div>
              <div className="ep-hero-email">{user?.email}</div>
            </div>
          </div>

          <div className="ep-body">

            {/* Basic Info */}
            <div className="ep-card">
              <div className="ep-card-header">
                <div className="ep-card-icon" style={{ background: 'rgba(109,40,217,0.12)' }}>
                  <Icons.User sz={16} style={{ color: 'var(--ac3)' }} />
                </div>
                <div>
                  <div className="ep-card-title">Basic Information</div>
                  <div className="ep-card-sub">Change how your name appears across aiField</div>
                </div>
              </div>
              <div className="ep-card-body">
                <div className="ep-field">
                  <label>Display Name</label>
                  <div className="ep-input-row">
                    <input
                      className="ep-input"
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Enter your name"
                      onKeyDown={e => e.key === 'Enter' && handleUpdateName()}
                    />
                    <button
                      className="ep-save-btn"
                      onClick={handleUpdateName}
                      disabled={updatingName || !name.trim() || name.trim() === user?.name}
                    >
                      {updatingName ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
                <div className="ep-field">
                  <label>Email Address</label>
                  <input
                    className="ep-input"
                    type="text"
                    value={user?.email || ''}
                    disabled
                    style={{ width: '100%' }}
                  />
                  <div className="ep-input-hint">Your email cannot be changed after registration.</div>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="ep-card">
              <div className="ep-card-header">
                <div className="ep-card-icon" style={{ background: 'rgba(16,185,129,0.12)' }}>
                  <Icons.Help sz={16} style={{ color: '#10b981' }} />
                </div>
                <div>
                  <div className="ep-card-title">Security</div>
                  <div className="ep-card-sub">Manage your password and account access</div>
                </div>
              </div>
              <div className="ep-card-body">
                <div className="ep-row">
                  <div className="ep-row-info">
                    <div className="ep-row-title">Password Reset</div>
                    <div className="ep-row-desc">
                      We'll send a secure reset link to <strong style={{ color: 'var(--t2)' }}>{user?.email}</strong>
                    </div>
                  </div>
                  <button className="ep-secondary-btn" onClick={handleResetPassword} disabled={resettingPw}>
                    {resettingPw ? 'Sending…' : 'Send Reset Email'}
                  </button>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="ep-danger-card">
              <div className="ep-danger-header">
                <div className="ep-card-icon" style={{ background: 'rgba(239,68,68,0.1)' }}>
                  <Icons.Trash sz={16} style={{ color: '#f87171' }} />
                </div>
                <div>
                  <div className="ep-card-title" style={{ color: '#f87171' }}>Danger Zone</div>
                  <div className="ep-card-sub">Irreversible and destructive actions</div>
                </div>
              </div>
              <div className="ep-danger-row">
                <div className="ep-row-info">
                  <div className="ep-row-title">Permanently Delete Account</div>
                  <div className="ep-row-desc">
                    Once you delete your account, all access, credits, and plan benefits are revoked immediately and cannot be recovered.
                  </div>
                </div>
                <button className="ep-danger-btn" onClick={() => setShowDeleteConfirm(true)}>
                  Delete Account
                </button>
              </div>
            </div>

            {/* Bottom padding */}
            <div style={{ height: 20 }} />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="mp-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)} style={{ zIndex: 80 }}>
          <div className="logout-confirm" onClick={e => e.stopPropagation()} style={{ borderColor: 'rgba(239,68,68,0.25)', maxWidth: 380 }}>
            <div className="logout-icon" style={{ fontSize: 44 }}>⚠️</div>
            <div className="logout-title" style={{ color: '#f87171', marginBottom: 8 }}>Delete Account?</div>
            <div className="logout-desc" style={{ marginBottom: 6 }}>
              This action is <strong style={{ color: 'var(--t2)' }}>permanent and irreversible</strong>.
              You will immediately lose:
            </div>
            <ul style={{ textAlign: 'left', fontSize: 12, color: 'var(--t3)', paddingLeft: 20, marginBottom: 22, lineHeight: 1.8 }}>
              <li>All remaining credits</li>
              <li>Your active subscription plan</li>
              <li>Access to your aiField account</li>
            </ul>
            <div className="logout-btns">
              <button className="logout-btn cancel" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </button>
              <button
                className="logout-btn confirm"
                onClick={handleDeleteAccount}
                disabled={deleting}
                style={{ background: '#ef4444', borderColor: '#ef4444', color: '#fff' }}
              >
                {deleting ? 'Deleting…' : 'Yes, delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
